import { useEffect, useRef, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import QRCode from 'qrcode';
import { supabase } from '@/lib/supabaseClient';
import { getPublicUrl } from '@/lib/publicUrl';
import { toast } from 'sonner';

type EmpresaInfo = {
  empresa: string;
  logo: string | null;
  descripcion: string | null;
  descripcion_corta: string | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  ciudad: string | null;
  categoria_slug: string;
  ciudad_slug: string;
  empresa_slug: string;
  color_primario: string | null;
  color_secundario: string | null;
};

export const useGenerateQR = () => {
  const [qrUrl, setQrUrl] = useState<string>('');
  const [qrCatalogoUrl, setQrCatalogoUrl] = useState<string>('');
  const [bookingUrl, setBookingUrl] = useState<string>('');
  const [catalogoUrl, setCatalogoUrl] = useState<string>('');
  const [reservasOn, setReservasOn] = useState(false);

  const [empresaInfo, setEmpresaInfo] = useState<EmpresaInfo | null>(null);
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [empresaLoaded, setEmpresaLoaded] = useState(false);

  const canvasCitasRef = useRef<HTMLCanvasElement>(null);
  const canvasCatalogoRef = useRef<HTMLCanvasElement>(null);

  // =====================
  // CARGA INICIAL
  // =====================
  useEffect(() => {
    const idE = localStorage.getItem('id_empresa');
    const idU = localStorage.getItem('id_usuario');

    if (!idE || !idU) {
      toast.error('No se encontraron datos de empresa o usuario');
      setLoading(false);
      return;
    }

    setEmpresaId(idE);
    setUserId(idU);

    (async () => {
      const { data: ent, error: entError } = await supabase
        .from('vista_empresa_detalle')
        .select(
          'empresa, logo, descripcion, descripcion_corta, telefono, email, direccion, ciudad, reservas_online_citas, categoria_slug, ciudad_slug, empresa_slug, color_primario, color_secundario'
        )
        .eq('id_empresa', idE)
        .single();

      if (entError) {
        Sentry.captureException(entError, { extra: { empresaId: idE } });
        toast.error('Error al cargar la empresa');
        setLoading(false);
        return;
      }

      if (ent) {
        // Nunca window.location.origin: en Capacitor devuelve localhost
        const base = getPublicUrl();

        setEmpresaInfo(ent);
        setReservasOn(ent.reservas_online_citas);
        setEmpresaLoaded(true);
        setBookingUrl(`${base}/client-booking/${idE}`);
        setCatalogoUrl(
          `${base}/${ent.categoria_slug}/${ent.ciudad_slug}/${ent.empresa_slug}`
        );
      }

      const { data: qrs, error: qrsError } = await supabase
        .from('qr_generados')
        .select('tipo_qr, url_archivo')
        .eq('id_empresa', idE);

      if (qrsError) {
        Sentry.captureException(qrsError, { extra: { empresaId: idE } });
      } else if (qrs) {
        const citas = qrs.find((q) => q.tipo_qr === 'citas');
        const catalogo = qrs.find((q) => q.tipo_qr === 'catalogo');
        if (citas) setQrUrl(citas.url_archivo);
        if (catalogo) setQrCatalogoUrl(catalogo.url_archivo);
      }

      setLoading(false);
    })();
  }, []);

  // =====================
  // GENERAR QR
  // =====================
  const generateQR = useCallback(
    async (tipo: 'citas' | 'catalogo') => {
      if (!empresaId || !userId) return;

      if (tipo === 'citas' && qrUrl) {
        toast.warning('Ya existe un QR de citas');
        return;
      }
      if (tipo === 'catalogo' && qrCatalogoUrl) {
        toast.warning('Ya existe un QR de catálogo');
        return;
      }

      setGenerating(true);

      try {
        const canvas =
          tipo === 'citas' ? canvasCitasRef.current : canvasCatalogoRef.current;

        if (!canvas) throw new Error('Canvas no disponible');

        const url = tipo === 'citas' ? bookingUrl : catalogoUrl;

        if (!url || url.includes('localhost')) {
          throw new Error('URL no válida para generar el QR');
        }

        const folder = tipo === 'citas' ? 'qr_citas' : 'qr_catalogos';
        const path = `qr_codes/${folder}/${empresaId}.png`;

        await QRCode.toCanvas(canvas, url, { width: 256 });

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve as any, 'image/png')
        );

        if (!blob) throw new Error('No se pudo generar el QR');

        const { error: uploadError } = await supabase.storage
          .from('bookoll')
          .upload(path, blob, { upsert: true });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from('bookoll').getPublicUrl(path);

        const { error: upsertError } = await supabase
          .from('qr_generados')
          .upsert(
            {
              id_empresa: empresaId,
              tipo_qr: tipo,
              url_archivo: data.publicUrl,
              usuario_creacion: userId,
              usuario_edicion: userId,
            },
            { onConflict: 'id_empresa,tipo_qr' }
          );

        if (upsertError) throw upsertError;

        if (tipo === 'citas') setQrUrl(data.publicUrl);
        if (tipo === 'catalogo') setQrCatalogoUrl(data.publicUrl);

        toast.success('QR generado correctamente');
      } catch (err) {
        Sentry.captureException(err, { extra: { empresaId, tipo } });
        toast.error((err as Error).message);
      } finally {
        setGenerating(false);
      }
    },
    [empresaId, userId, qrUrl, qrCatalogoUrl, bookingUrl, catalogoUrl]
  );

  // =====================
  // TOGGLE RESERVAS
  // =====================
  const toggleReservas = useCallback(async () => {
    if (!empresaId) return;

    const next = !reservasOn;
    setReservasOn(next);

    const { error } = await supabase
      .from('empresas')
      .update({ reservas_online_citas: next, usuario_edicion: userId })
      .eq('id_empresa', empresaId);

    if (error) {
      Sentry.captureException(error, { extra: { empresaId, next } });
      setReservasOn(!next);
      toast.error('Error al actualizar reservas');
    } else {
      toast.success(`Reservas ${next ? 'activadas' : 'desactivadas'}`);
    }
  }, [empresaId, reservasOn, userId]);

  // =====================
  // ELIMINAR QR
  // =====================
  const removeQR = useCallback(
    async (tipo: 'citas' | 'catalogo') => {
      if (!empresaId) return;

      const folder = tipo === 'citas' ? 'qr_citas' : 'qr_catalogos';
      const path = `qr_codes/${folder}/${empresaId}.png`;

      const { error: removeError } = await supabase.storage
        .from('bookoll')
        .remove([path]);

      if (removeError) {
        Sentry.captureException(removeError, { extra: { empresaId, tipo } });
      }

      const { error: deleteError } = await supabase
        .from('qr_generados')
        .delete()
        .eq('id_empresa', empresaId)
        .eq('tipo_qr', tipo);

      if (deleteError) {
        Sentry.captureException(deleteError, { extra: { empresaId, tipo } });
      }

      if (tipo === 'citas') setQrUrl('');
      if (tipo === 'catalogo') setQrCatalogoUrl('');

      toast.success(`QR de ${tipo} eliminado`);
    },
    [empresaId]
  );

  // =====================
  // GUARDAR COLORES
  // =====================
  const saveColors = useCallback(
    async (colorPrimario: string, colorSecundario: string) => {
      if (!empresaId) return;

      setSavingColors(true);

      const { error } = await supabase
        .from('empresas')
        .update({
          color_primario: colorPrimario,
          color_secundario: colorSecundario,
          usuario_edicion: userId,
          fecha_edicion: new Date(),
        })
        .eq('id_empresa', empresaId);

      if (error) {
        Sentry.captureException(error, { extra: { empresaId } });
        toast.error('Error al guardar los colores');
      } else {
        setEmpresaInfo((prev) =>
          prev
            ? {
                ...prev,
                color_primario: colorPrimario,
                color_secundario: colorSecundario,
              }
            : prev
        );
        toast.success('Colores guardados correctamente');
      }

      setSavingColors(false);
    },
    [empresaId, userId]
  );

  // =====================
  // DESCARGAR / COPIAR
  // =====================
  const downloadQR = useCallback(async (url: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const link = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link;
      a.download = 'codigo_qr.png';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(link);
      toast.success('QR descargado');
    } catch (err) {
      Sentry.captureException(err, { extra: { url } });
      toast.error('No se pudo descargar el QR');
    }
  }, []);

  const copyLink = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado');
    } catch (err) {
      Sentry.captureException(err, { extra: { url } });
      toast.error('No se pudo copiar el enlace');
    }
  }, []);

  // =====================
  // RETURN
  // =====================
  return {
    qrUrl,
    qrCatalogoUrl,
    bookingUrl,
    catalogoUrl,
    reservasOn,
    loading,
    generating,
    savingColors,
    empresaLoaded,
    empresaInfo,
    empresaId,

    canvasCitasRef,
    canvasCatalogoRef,

    generateQR,
    toggleReservas,
    removeQR,
    downloadQR,
    copyLink,
    saveColors,
  };
};